import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import UserDashboard from "../pages/hotspot/dashboard";
import PortalLayout from "../pages/hotspot/hotspotLayout";
import PortalPlans from "../pages/hotspot/hotspotPlans";
import PortalSuccess from "../pages/hotspot/hotspotSucess";
import PortalLogin from "../pages/hotspot/portalLogin";
import PortalRegister from "../pages/hotspot/registerPage";

import LoginPage from "../pages/auth/Login";

import FinanceDashboardPage from "../pages/dashboard/Dashboard";
import MikrotikDashboardPage from "../pages/dashboard/MikrotikDashboardPage";

import ActiveConnectionsPage from "../pages/customers/ActiveConnectionsPage";
import AddCustomerPage from "../pages/customers/AddCustomerPage";
import CustomerDetailsPage from "../pages/customers/CustomerDetailPage";
import CustomerEditPage from "../pages/customers/CustomerEditPage";
import CustomersPage from "../pages/customers/CustomerPage";
import ExtendedCustomersPage from "../pages/customers/ExtendedCustomers";
import HotspotUsersPage from "../pages/customers/hotspot/hotspotCustomers";
import PPPoEBillingGroupsPage from "../pages/pppoe-billing-groups/PPPoEBillingGroupsPage";
import ExpiringSubscriptionsPage from "../pages/subscriptions/ExpiringSubscriptionsPage";

import HotspotPlansPage from "../pages/plans/HotspotPlans";
import PlansPage from "../pages/plans/internetPlans";

import ExpensesPage from "../pages/expenses/ExpensesPage";
import HotspotPaymentsPage from "../pages/finance/hotspot/HotspotPaymentPage";
import ErrorMpesaTransactionsPage from "../pages/finance/ppoe/DefaultyTransactions";
import InvoicesPage from "../pages/finance/ppoe/InvoicesPage";
import MpesaTransactionsPage from "../pages/finance/ppoe/MpesaTransactionsPage";
import PaymentsPage from "../pages/finance/ppoe/PaymentsPage";

import IPAddressesPage from "../ip adresses/IpAdressesPage";
import BridgeDetailPage from "../pages/bridges/BridgeDetailPage";
import BridgesPage from "../pages/bridges/BridgePage";
import InterfaceDetailPage from "../pages/interfaces/InterfaceDetailPage";
import InterfacesPage from "../pages/interfaces/interfacesPage";
import PoolsPage from "../pages/pools/PoolsPage";
import PPPoEServersPage from "../pages/pppoe_server/PPPoEPage";
import RoutersPage from "../pages/super_admin/routers/RoutersPage";

import RouterBackUpPage from "../pages/router/RouterBackUpPage";
import TerminalPage from "../pages/terminal/TerminalPage";
import LiveTerminal from "../pages/terminal/liveTerminal";

import CustomerMessagesDetailPage from "../pages/sms/CustomerMessagesDetailPage";
import MessagesListPage from "../pages/sms/MessagesListPage";
import MessagesPage from "../pages/sms/MessagesPage";
import SmsTemplatesPage from "../pages/sms/SmsTemplatesPage";

import TransactionReportPage from "../pages/reports/TransactionReportPage";

import ClientsPage from "../pages/clients/ClientsList";
import RolesPage from "../pages/roles/rolePage";

import GenericPage from "../pages/generic/GenericPage";
import ProtectedRoute from "./ProtectedRoute";

import CustomerCareDashboard from "../pages/customer-care/CustomerCareDashboard";
import NCBATransactionsPage from "../pages/finance/ppoe/NCBATransactionsPage";
import ResellerPartnerDetailPage from "../pages/reseller/ResellerPartnerDetailPage";
import ResellerPartnersPage from "../pages/reseller/ResellerPartnersPage";
import ApiLogsPage from "../pages/super_admin/logger/ApiLogsPage";
import CustomerCareUsersPage from "../pages/support/CustomerCareUsersPage";
import SupportTicketDetailPage from "../pages/support_ticket/SupportTicketDetailPage";
import SupportTicketPage from "../pages/support_ticket/SupportTicketPage";
import TechnicianDetailPage from "../pages/technicians/TechnicianDetailPage";
import TechniciansPage from "../pages/technicians/TechnicianPage";
import { DashboardData } from "../types";

interface AppRoutesProps {
  data: DashboardData;
}

const AppRoutes: React.FC<AppRoutesProps> = ({ data }) => {
  const ADMINS = ["admin"];

  return (
    <Routes>
      {/* ========================================================= */}
      {/* 🌐 PUBLIC HOTSPOT PORTAL ROUTES                           */}
      {/* ========================================================= */}

      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<Navigate to="login" replace />} />
        <Route path="register" element={<PortalRegister />} />
        <Route path="login" element={<PortalLogin />} />
        <Route path="plans" element={<PortalPlans />} />
        <Route path="active" element={<PortalSuccess />} />
      </Route>

      <Route path="/my-account" element={<UserDashboard />} />

      {/* ========================================================= */}
      {/* 🔐 AUTH ROUTES                                            */}
      {/* ========================================================= */}

      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={<Navigate to="/overview" replace />} />

      {/* ========================================================= */}
      {/* 📊 DASHBOARD ROUTES                                       */}
      {/* ========================================================= */}

      <Route
        path="/customer-care"
        element={
          <ProtectedRoute allowedRoles={["customer_care"]}>
            <CustomerCareDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/overview"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <FinanceDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/overview/mikrotik"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <MikrotikDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 👥 CUSTOMER MANAGEMENT                                    */}
      {/* ========================================================= */}

      <Route
        path="/customers"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <CustomersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/add"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <AddCustomerPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <CustomerDetailsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/:id/edit"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <CustomerEditPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/active-connections"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <ActiveConnectionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/expiring"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <ExpiringSubscriptionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/extended"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <ExtendedCustomersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pppoe-billing-groups"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <PPPoEBillingGroupsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/hotspot"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <HotspotUsersPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 🌍 PLANS & SERVICES                                       */}
      {/* ========================================================= */}

      <Route
        path="/plans"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <PlansPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/hotspot/plans"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <HotspotPlansPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 💰 FINANCE & BILLING                                      */}
      {/* ========================================================= */}

      <Route
        path="/finance/invoices"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance/payments"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <PaymentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance/mpesa"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <MpesaTransactionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance/error"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <ErrorMpesaTransactionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance/hotspot/payments"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <HotspotPaymentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance/expenses"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <ExpensesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance/ncba"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <NCBATransactionsPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 🌐 NETWORK MANAGEMENT                                     */}
      {/* ========================================================= */}

      <Route
        path="/routers"
        element={
          <ProtectedRoute allowedRoles={["super admin"]}>
            <RoutersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/interfaces"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <InterfacesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/interfaces/:routerId/:name"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <InterfaceDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/bridges"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <BridgesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/bridges/:name"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <BridgeDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pools"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <PoolsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pppoe"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <PPPoEServersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ip-address"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <IPAddressesPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 🖥️ TERMINAL & MIKROTIK TOOLS                             */}
      {/* ========================================================= */}

      <Route
        path="/terminal"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <TerminalPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/terminal/logs"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <LiveTerminal />
          </ProtectedRoute>
        }
      />

      <Route
        path="/mikrotik/backup"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <RouterBackUpPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 📡 NETWORK MONITORING                                     */}
      {/* ========================================================= */}

      <Route
        path="/monitoring"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Network Monitoring" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/alerts"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Network Alerts" />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 💬 COMMUNICATION                                          */}
      {/* ========================================================= */}

      <Route
        path="/sms"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <MessagesListPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/messages/send"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/messages/customer/:customerId"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <CustomerMessagesDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/sms/templates"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <SmsTemplatesPage />
          </ProtectedRoute>
        }
      />
      {/* =========================Partnertship======================== */}
      <Route
        path="/reseller-partners"
        element={
          <ProtectedRoute
            allowedRoles={["admin"]}
            allowedUsers={[
              "df0a7017-592a-4468-ac2e-52873ede024b",
              "d0174f85-b4c9-4a36-832f-540f8f624616",
              "131ac96d-8088-419c-8649-5a90a60b1ebd",
            ]}
          >
            <ResellerPartnersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller-partners/:id"
        element={
          <ProtectedRoute
            allowedRoles={["admin"]}
            allowedUsers={[
              "df0a7017-592a-4468-ac2e-52873ede024b",
              "d0174f85-b4c9-4a36-832f-540f8f624616",
              "131ac96d-8088-419c-8649-5a90a60b1ebd",
            ]}
          >
            <ResellerPartnerDetailPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 🛠️ SUPPORT & STAFF                                        */}
      {/* ========================================================= */}

      <Route
        path="/support/customer-care-users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CustomerCareUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/technicians"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <TechniciansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/technicians/:technicianId"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <TechnicianDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/support/tickets"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <SupportTicketPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/support/tickets/:ticketId"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <SupportTicketDetailPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 📈 REPORTS                                                */}
      {/* ========================================================= */}

      <Route
        path="/timely-report"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <TransactionReportPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* ⚙️ SYSTEM SETTINGS                                        */}
      {/* ========================================================= */}

      <Route
        path="/roles"
        element={
          <ProtectedRoute allowedRoles={["super admin"]}>
            <RolesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/general"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="General Settings" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/domains"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Domains" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/integrations"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Integrations" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Billing" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/payouts"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Payouts" />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 🏢 SUPER ADMIN                                            */}
      {/* ========================================================= */}

      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={["super admin"]}>
            <ClientsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute allowedRoles={["super admin"]}>
            <ApiLogsPage />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* 📚 HELP & DOCUMENTATION                                   */}
      {/* ========================================================= */}

      <Route
        path="/documentation"
        element={
          <ProtectedRoute allowedRoles={ADMINS}>
            <GenericPage title="Documentation" />
          </ProtectedRoute>
        }
      />

      {/* ========================================================= */}
      {/* ❌ 404 PAGE                                               */}
      {/* ========================================================= */}

      <Route
        path="*"
        element={
          <ProtectedRoute>
            <GenericPage
              title="Page Not Found"
              description="The page you are looking for does not exist."
            />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
